import { FC, useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-location';
import { Header } from 'components';
import { ClipboardDocumentListIcon, ClockIcon, CheckCircleIcon, ExclamationCircleIcon, KeyIcon, DocumentArrowDownIcon } from '@heroicons/react/24/outline';
import { useAuthStore } from 'state/auth';
import useApi from 'utils/use-api';
import dayjs from 'dayjs';
import GenerateReportModal from 'components/forms/generate-report-modal';
import wrapClick from 'utils/wrap-click';
import withPermissions from 'utils/with-permissions';

const RecentRequestsTable: FC = () => {
  const api = useApi();
  const navigate = useNavigate();

  const { data: recentRequests, isLoading } = useQuery({
    queryKey: ['recent-access-requests'],
    queryFn: async () => {
      const response = await api.get('/access-requests', {
        params: { skip: 0, limit: 5 },
      });
      return response.data;
    },
  });

  const statusColors: Record<string, string> = {
    open: 'bg-blue-50 text-blue-700 ring-blue-600/20',
    reopened: 'bg-purple-50 text-purple-700 ring-purple-600/20',
    processing: 'bg-yellow-50 text-yellow-700 ring-yellow-600/20',
    more_info: 'bg-orange-50 text-orange-700 ring-orange-600/20',
    complete: 'bg-green-50 text-green-700 ring-green-600/20',
    closed: 'bg-gray-50 text-gray-700 ring-gray-600/20',
  };

  const statusLabels: Record<string, string> = {
    open: 'Open',
    reopened: 'Re-Opened',
    processing: 'Processing',
    more_info: 'More Info Needed',
    complete: 'Complete',
    closed: 'Closed',
  };

  const formatDate = (dateString: string) => {
    try {
      return dayjs(dateString).format('MMM DD, YYYY');
    } catch {
      return dateString;
    }
  };

  if (isLoading) {
    return (
      <div className="bg-white rounded-lg shadow p-6">
        <p className="text-center text-gray-500">Loading recent requests...</p>
      </div>
    );
  }

  if (!recentRequests?.rows || recentRequests.rows.length === 0) {
    return (
      <div className="bg-white rounded-lg shadow p-6">
        <p className="text-center text-gray-500">No recent requests</p>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow overflow-hidden">
      <table className="min-w-full divide-y divide-gray-200">
        <thead className="bg-gray-50">
          <tr>
            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Request ID
            </th>
            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Category
            </th>
            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Status
            </th>
            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Date
            </th>
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-gray-200">
          {recentRequests.rows.map((request: any) => (
            <tr key={request.id} onClick={() => navigate({ to: '/access-requests' })} className="hover:bg-gray-50 cursor-pointer">
              <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">#{request.id}</td>
              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{request.category}</td>
              <td className="px-6 py-4 whitespace-nowrap">
                <span
                  className={`inline-flex items-center rounded-md px-2 py-1 text-xs font-medium ring-1 ring-inset ${statusColors[request.status]}`}
                >
                  {statusLabels[request.status]}
                </span>
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{formatDate(request.createdAt)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

const DashboardPage: FC = () => {
  useEffect(() => {
    document.title = 'Dashboard';
  }, []);
  const api = useApi();
  const navigate = useNavigate();
  const [showReportModal, setShowReportModal] = useState(false);

  const [stats, setStats] = useState([
    {
      name: 'Total Requests',
      stat: '0',
      totalColor: '#3B82F6',
      percentageColor: '#2563EB',
      bgColor: 'bg-[#3B82F6]',
      icon: ClipboardDocumentListIcon,
      link: '/access-requests',
    },
    {
      name: 'Open Requests',
      stat: '0',
      totalColor: '#F59E0B',
      percentageColor: '#D97706',
      bgColor: 'bg-[#F59E0B]',
      icon: ClockIcon,
      link: '/access-requests',
    },
    {
      name: 'Completed',
      stat: '0',
      totalColor: '#10B981',
      bgColor: 'bg-[#10B981]',
      percentageColor: '#059669',
      icon: CheckCircleIcon,
      link: '/access-requests',
    },
    {
      name: 'Pending Action',
      stat: '0',
      totalColor: '#EF4444',
      bgColor: 'bg-[#EF4444]',
      percentageColor: '#DC2626',
      icon: ExclamationCircleIcon,
      link: '/access-requests',
    },
  ]);

  const {
    data: dashboardStat,
    isLoading: dashboardStatLoading,
    refetch: refetchStat,
  } = useQuery({
    queryKey: ['dashboard-stats'],
    queryFn: async () => {
      const response = await api.get('/stats/dashboard');
      return response.data;
    },
  });

  const { data: accessRequestStats, isLoading: accessRequestStatsLoading } = useQuery({
    queryKey: ['access-request-stats'],
    queryFn: async () => {
      const response = await api.get('/access-requests/stats');
      return response.data;
    },
  });

  const currentUser = useAuthStore(state => state.user);

  useEffect(() => {
    if (accessRequestStats) {
      setStats(prevStats =>
        prevStats.map((stat, index) => {
          if (index === 0) {
            return { ...stat, stat: accessRequestStats.total || 0 };
          } else if (index === 1) {
            return { ...stat, stat: accessRequestStats.open || 0 };
          } else if (index === 2) {
            return { ...stat, stat: accessRequestStats.completed || 0 };
          } else if (index === 3) {
            return { ...stat, stat: accessRequestStats.pendingAction || 0 };
          }
          return stat;
        }),
      );
    }
  }, [accessRequestStats]);

  useEffect(() => {
    refetchStat();
  }, [refetchStat]);

  const recentActivations = dashboardStat?.recentActivations ?? [];
  const pendingInvites = dashboardStat?.pendingInvites ?? 0;
  const totalActivated = dashboardStat?.totalActivated ?? 0;

  const renderHeaderActions = () => {
    const checkPermission = withPermissions(['system:admin']);

    return checkPermission(
      <button
        type="button"
        onClick={wrapClick(() => setShowReportModal(true))}
        className="inline-flex items-center gap-x-2 rounded-md bg-primary-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-primary-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-600"
      >
        <DocumentArrowDownIcon className="-ml-0.5 h-5 w-5" aria-hidden="true" />
        Generate Report
      </button>
    );
  };

  return (
    <main className="flex-1 flex flex-col overflow-hidden h-screen bg-gray-50">
      <Header renderActions={renderHeaderActions} />
      <div className="flex flex-1 overflow-y-auto">
        <div className="flex-1 min-h-full mx-auto  p-4 sm:p-6 overflow-y-auto bg-gray-50">
          <div className="grid grid-cols-1 items-start gap-4 lg:grid-cols-12 lg:gap-8">
            <div className="col-span-12">
              <div className=" flex flex-col">
                <h1 className="font-bold text-3xl">Welcome Back, {currentUser?.firstName || 'N/A'}</h1>
                <p className="text-sm text-gray-500">Track and manage your requests</p>
              </div>
              <div className="flex flex-col lg:flex-row mt-10 lg:justify-between lg:max-w-full lg:gap-x-6 lg:overflow-x-auto scroll-smooth pt-1 gap-y-5">
                {stats.map(item => {
                  const Icon = item.icon as React.ElementType;
                  return (
                    <div
                      key={item.name}
                      onClick={() => item.link && navigate({ to: item.link })}
                      className="bg-white md:min-w-[15rem] h-32 border flex items-center justify-between gap-x-10 w-full rounded-lg shadow-md p-4 cursor-pointer hover:shadow-lg transition-shadow"
                    >
                      <div className="flex-1 flex flex-col gap-y-5">
                        <p className="font-medium text-base font-manrope">{item.name}</p>
                        <p className="text-2xl font-manrope font-500">{accessRequestStatsLoading ? 'Loading...' : item.stat}</p>
                      </div>

                      <div className={`flex justify-center items-center w-12 h-12 rounded-full ${item.bgColor} bg-opacity-80`}>
                        {Icon ? <Icon className="w-5 h-5 text-white" /> : null}
                      </div>
                    </div>
                  );
                })}
                {['*', 'system:admin'].some(p => currentUser?.role?.permissions?.includes(p as any)) && (
                  <div className="bg-white md:min-w-[15rem] h-32 border flex items-center justify-between gap-x-10 w-full rounded-lg shadow-md p-4">
                    <div className="flex-1 flex flex-col gap-y-5">
                      <p className="font-medium text-base font-manrope">Pending Activations</p>
                      <p className="text-2xl font-manrope font-500">{dashboardStatLoading ? 'Loading...' : pendingInvites}</p>
                    </div>
                    <div className="flex justify-center items-center w-12 h-12 rounded-full bg-primary-600 bg-opacity-80">
                      <KeyIcon className="w-5 h-5 text-white" />
                    </div>
                  </div>
                )}
              </div>
              <div className="mt-10">
                <div className="flex items-center justify-between mb-4">
                  <h1 className="font-bold text-lg text-gray-700">Recent Access Requests</h1>
                  <button
                    onClick={() => navigate({ to: '/access-requests' })}
                    className="text-sm text-primary-600 hover:text-primary-800 font-medium"
                  >
                    View all →
                  </button>
                </div>
                <RecentRequestsTable />
                {['*', 'system:admin'].some(p => currentUser?.role?.permissions?.includes(p as any)) && (
                  <div className="mt-8 bg-white shadow rounded-lg p-6">
                    <div className="flex items-center justify-between mb-4">
                      <h2 className="text-lg font-semibold text-gray-900">Recent Activations</h2>
                      <p className="text-sm text-gray-500">Total activated: {totalActivated}</p>
                    </div>
                    {dashboardStatLoading ? (
                      <p className="text-gray-500">Loading...</p>
                    ) : recentActivations.length === 0 ? (
                      <p className="text-gray-500">No activations yet.</p>
                    ) : (
                      <ul className="divide-y divide-gray-200">
                        {recentActivations.map((user: any) => (
                          <li key={user.id} className="py-3 flex items-center justify-between">
                            <div>
                              <p className="text-sm font-medium text-gray-900">
                                {[user.firstName, user.lastName].filter(Boolean).join(' ') || user.email}
                              </p>
                              <p className="text-sm text-gray-500">{user.email}</p>
                            </div>
                            <div className="text-sm text-gray-500">
                              {user.passwordChangedAt ? dayjs(user.passwordChangedAt).format('MMM DD, YYYY HH:mm') : '—'}
                            </div>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      <GenerateReportModal open={showReportModal} setOpen={setShowReportModal} />
    </main>
  );
};

export default DashboardPage;
